import Route from '@ember/routing/route';
import { getOwner } from '@ember/owner';
import { generateHomePageStructuredData } from 'madow-tech/utils/schema-org';
import type MetaService from 'madow-tech/services/meta';

export default class EsRoute extends Route {
  model() {
    // Get intl service
    const owner = getOwner(this);
    if (!owner) return {};

    const intl = owner.lookup('service:intl');
    if (!intl) return {};

    // Set locale to Spanish
    intl.setLocale(['es']);

    // Persist locale preference in localStorage (preferred) or cookie (fallback)
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        window.localStorage.setItem('locale', 'es');
      } catch (e) {
        // localStorage might be disabled, fall back to cookie
        if (typeof document !== 'undefined') {
          document.cookie = `locale=es;path=/;max-age=31536000`;
        }
      }
    } else if (typeof document !== 'undefined') {
      document.cookie = `locale=es;path=/;max-age=31536000`;
    }

    // Set up SEO metadata
    this.setupSEO(owner);

    return {};
  }

  private setupSEO(owner: ReturnType<typeof getOwner>) {
    const meta = owner?.lookup('service:meta') as MetaService | undefined;
    if (!meta) return;

    // Reset previous meta
    meta.reset();

    // Set basic metadata in Spanish
    meta.title = 'Software que funciona, hecho por gente que le importa';
    meta.description =
      'Un equipo joven de desarrolladores y diseñadores experimentados que construyen plataformas web, aplicaciones móviles y productos digitales que realmente funcionan. Desde Argentina al mundo.';
    meta.canonicalUrl = 'https://madow.tech/es';

    // Set hreflang alternate languages
    meta.alternateLanguages = [
      { lang: 'en', url: 'https://madow.tech/en' },
      { lang: 'es', url: 'https://madow.tech/es' },
    ];

    // Set custom meta tags
    meta.setTags([
      {
        name: 'keywords',
        content:
          'desarrollo de software, desarrollo web, aplicaciones móviles, aumento de equipo, TypeScript, Ember.js, React, NestJS, Argentina, nearshore, desarrolladores LATAM',
      },
      { property: 'og:locale', content: 'es_AR' },
      { property: 'og:locale:alternate', content: 'en_US' },
      { name: 'language', content: 'Spanish' },
      { name: 'geo.region', content: 'AR-X' },
      { name: 'geo.placename', content: 'Córdoba' },
    ]);

    // Add structured data
    meta.scriptTags = [
      {
        type: 'application/ld+json',
        id: 'structured-data-main',
        content: generateHomePageStructuredData('es'),
      },
    ];

    // Render meta to header
    meta.renderMetaToHeader();
  }
}
